import { useState, useEffect } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Search, Building2, Users, Calendar, CreditCard, TrendingUp, MoreVertical, ArrowUpCircle, RefreshCw, Ban, CheckCircle2, Eye, Mail, Bell } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { useSuperAdmin } from "@/hooks/use-super-admin";
import { useNavigate } from "react-router-dom";
import { SuperAdminSubscriptionDialog } from "@/components/SuperAdminSubscriptionDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { captureException } from "@/lib/sentry";

interface BusinessWithMetrics {
  id: string;
  name: string;
  slug: string;
  owner_user_id: string;
  created_at: string;
  subscription_status: string;
  plan_id: string | null;
  plan_name: string | null;
  owner_email: string | null;
  owner_name: string | null;
  primary_module: string | null;
  // Metrics
  totalAppointments: number;
  totalCustomers: number;
  totalRevenue: number;
  totalPayments: number;
  pendingPayouts: number;
}

export default function SuperAdminDashboard() {
  const { isSuperAdmin } = useSuperAdmin();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [businesses, setBusinesses] = useState<BusinessWithMetrics[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessWithMetrics | null>(null);
  const [dialogMode, setDialogMode] = useState<"upgrade" | "renew" | "suspend" | "activate">("upgrade");
  const [subscriptionRevenue, setSubscriptionRevenue] = useState(0);
  const [previewSendingBusinessId, setPreviewSendingBusinessId] = useState<string | null>(null);
  const [pushTestKey, setPushTestKey] = useState<string | null>(null);

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate("/app");
      return;
    }
    fetchBusinesses();
  }, [isSuperAdmin, navigate]);

  const fetchBusinesses = async () => {
    try {
      setLoading(true);
      
      // Fetch all businesses with owner info
      const { data: businessesData, error: businessesError } = await supabase
        .from("businesses")
        .select(`
          id,
          name,
          slug,
          owner_user_id,
          created_at,
          subscription_status,
          plan_id,
          primary_module
        `)
        .order("created_at", { ascending: false });

      if (businessesError) throw businessesError;

      // Platform revenue from subscription payments (businesses paying the platform for plans)
      const { data: subscriptionPayments } = await supabase
        .from("platform_payments")
        .select("gross_amount")
        .eq("context", "platform_subscription_setup")
        .eq("status", "success");
      const subRevenue = (subscriptionPayments || []).reduce(
        (sum: number, p: any) => sum + Number(p.gross_amount || 0),
        0
      );
      setSubscriptionRevenue(subRevenue);

      // Fetch subscription plans
      const { data: plansData } = await supabase
        .from("subscription_plans")
        .select("id, name");

      const plansMap = new Map(plansData?.map(p => [p.id, p.name]) || []);

      // Fetch owner emails
      const ownerIds = businessesData?.map(b => b.owner_user_id) || [];
      const { data: profilesData } = await supabase
        .from("profiles")
        .select("id, email, name")
        .in("id", ownerIds);

      const profilesMap = new Map(profilesData?.map(p => [p.id, p]) || []);

      // Fetch metrics for each business
      const businessesWithMetrics = await Promise.all(
        (businessesData || []).map(async (business) => {
          const profile = profilesMap.get(business.owner_user_id);
          
          // Get appointments count
          const { count: appointmentsCount } = await supabase
            .from("appointments")
            .select("*", { count: "exact", head: true })
            .eq("business_id", business.id);

          // Get customers count (unique customers per business)
          const { count: customersCount } = await supabase
            .from("customers")
            .select("*", { count: "exact", head: true })
            .eq("business_id", business.id);

          // Calculate revenue from multiple sources (matching use-dashboard-stats logic)
          // 1. Completed appointments revenue (full price, not deposits)
          const { data: completedAppointments } = await supabase
            .from("appointments")
            .select("total_price_amount, service:services(price_amount)")
            .eq("business_id", business.id)
            .eq("status", "completed");

          const appointmentRevenue = (completedAppointments || []).reduce(
            (sum, a: any) => sum + (Number(a.total_price_amount) || Number(a.service?.price_amount) || 0),
            0
          );

          // 2. Invoice revenue (paid invoices)
          const { data: paidInvoices } = await supabase
            .from("invoices")
            .select("id, invoice_items(*), invoice_adjustments(*)")
            .eq("business_id", business.id)
            .eq("status", "paid");

          let invoiceRevenue = 0;
          if (paidInvoices) {
            for (const inv of paidInvoices) {
              const items = (inv as any).invoice_items || [];
              const adjustments = (inv as any).invoice_adjustments || [];
              const subtotal = items.reduce(
                (s: number, it: any) => s + Number(it.quantity || 0) * Number(it.unit_price || 0),
                0
              );
              const taxes = adjustments
                .filter((a: any) => a.type === "tax" && !a.included_in_prices)
                .reduce((s: number, a: any) => {
                  if (a.mode === "percent") {
                    return s + (subtotal * Number(a.percent_rate || 0)) / 100;
                  }
                  return s + Number(a.amount || 0);
                }, 0);
              const discounts = adjustments
                .filter((a: any) => a.type === "discount")
                .reduce((s: number, a: any) => {
                  if (a.mode === "percent") {
                    return s + (subtotal * Number(a.percent_rate || 0)) / 100;
                  }
                  return s + Number(a.amount || 0);
                }, 0);
              invoiceRevenue += subtotal + taxes - discounts;
            }
          }

          // 3. Walk-in revenue (day passes)
          const { data: dayPasses } = await supabase
            .from("day_passes")
            .select("amount")
            .eq("business_id", business.id)
            .eq("payment_status", "paid");

          const walkInRevenue = (dayPasses || []).reduce(
            (sum, dp: any) => sum + Number(dp.amount || 0),
            0
          );

          // 4. Membership revenue (from platform_payments)
          const { data: membershipPayments } = await supabase
            .from("platform_payments")
            .select("gross_amount")
            .eq("business_id", business.id)
            .eq("context", "membership_payment")
            .eq("status", "success");

          const membershipRevenue = (membershipPayments || []).reduce(
            (sum, p: any) => sum + Number(p.gross_amount || 0),
            0
          );

          // Total revenue
          const totalRevenue = appointmentRevenue + invoiceRevenue + walkInRevenue + membershipRevenue;

          // Get payments count and pending payouts
          const { data: paymentsData } = await supabase
            .from("platform_payments")
            .select("payout_status")
            .eq("business_id", business.id)
            .eq("status", "success");

          const totalPayments = paymentsData?.length || 0;
          const pendingPayouts = paymentsData?.filter(p => p.payout_status === "pending" || p.payout_status === "eligible").length || 0;

          return {
            ...business,
            plan_name: plansMap.get(business.plan_id || "") || null,
            owner_email: profile?.email || null,
            owner_name: profile?.name || null,
            totalAppointments: appointmentsCount || 0,
            totalCustomers: customersCount || 0,
            totalRevenue,
            totalPayments,
            pendingPayouts,
          };
        })
      );

      setBusinesses(businessesWithMetrics);
    } catch (error) {
      console.error("Error fetching businesses:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredBusinesses = businesses.filter(business =>
    business.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    business.owner_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    business.slug.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sendPushTestSuite = async (business: BusinessWithMetrics, scope: "all" | "owner") => {
    const key = `${business.id}:${scope}`;
    try {
      setPushTestKey(key);
      const { data, error } = await supabase.functions.invoke("superadmin-push-test-suite", {
        body: {
          business_id: business.id,
          ...(scope === "owner" && business.owner_user_id
            ? { user_id: business.owner_user_id }
            : {}),
        },
      });
      if (error) throw error;
      const total = typeof (data as any)?.total_sent_across_scenarios === "number"
        ? (data as any).total_sent_across_scenarios
        : 0;
      toast({
        title: "Push test suite finished",
        description:
          scope === "owner"
            ? `${business.name} (owner): ${total} send(s) across ${(data as any)?.scenarios_run ?? "?"} scenarios.`
            : `${business.name}: ${total} send(s) across ${(data as any)?.scenarios_run ?? "?"} scenarios.`,
      });
    } catch (error) {
      console.error("Push test suite failed:", error);
      toast({
        title: "Push test suite failed",
        description: error instanceof Error ? error.message : "Could not run push test suite.",
        variant: "destructive",
      });
    } finally {
      setPushTestKey(null);
    }
  };

  const sendWelcomePreview = async (business: BusinessWithMetrics) => {
    try {
      setPreviewSendingBusinessId(business.id);
      const { error } = await supabase.functions.invoke("send-email", {
        body: {
          type: "welcome_account_confirmed_preview",
          businessId: business.id,
        },
      });

      if (error) throw error;

      toast({
        title: "Preview sent",
        description: `Welcome email preview sent for ${business.name}.`,
      });
    } catch (error) {
      console.error("Failed to send welcome email preview:", error);
      toast({
        title: "Preview failed",
        description: error instanceof Error ? error.message : "Could not send welcome email preview.",
        variant: "destructive",
      });
    } finally {
      setPreviewSendingBusinessId(null);
    }
  };

  const totalBusinesses = businesses.length;
  const totalRevenue = businesses.reduce((sum, b) => sum + b.totalRevenue, 0);
  const totalAppointments = businesses.reduce((sum, b) => sum + b.totalAppointments, 0);
  const totalCustomers = businesses.reduce((sum, b) => sum + b.totalCustomers, 0);

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <AppShell businessName="Super Admin">
      <PageHeader
        title="Super Admin Dashboard"
        description="Manage all businesses and platform operations"
        action={
          <Button asChild variant="outline">
            <Link to="/app/super-admin/payments">
              <CreditCard className="mr-2 h-4 w-4" />
              View Payments
            </Link>
          </Button>
        }
      />

      {/* Sentry Test Section - Only for super admin */}
      {import.meta.env.VITE_SENTRY_DSN && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Test Sentry Integration</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-4">
              Click the buttons below to send test errors to Sentry and verify it's working.
              {import.meta.env.DEV && !import.meta.env.VITE_SENTRY_ENABLE_DEV && (
                <span className="block mt-1 text-warning">
                  Note: Sentry is disabled in development. Set VITE_SENTRY_ENABLE_DEV=true to enable.
                </span>
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const testError = new Error("Test error from Buukd Super Admin Dashboard - Sentry verification");
                  testError.name = "SentryTestError";
                  
                  if (!testError.stack) {
                    testError.stack = new Error().stack;
                  }
                  
                  captureException(testError, {
                    test: true,
                    source: "super_admin_dashboard",
                    user: user?.email || "unknown",
                    timestamp: new Date().toISOString(),
                    verification: true,
                  });
                  
                  toast({
                    title: "Test error sent",
                    description: "Check your Sentry dashboard.",
                  });
                }}
                className="w-full sm:w-auto"
              >
                Send Test Error to Sentry
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={() => {
                  throw new Error("This is your first error!");
                }}
                className="w-full sm:w-auto"
              >
                Break the world (Sentry Example)
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Businesses</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalBusinesses}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Businesses&apos; revenue</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₵{totalRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">From appointments, invoices, day passes, memberships</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Subscription revenue</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">₵{subscriptionRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground mt-1">From subscription plans (platform income)</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Appointments</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalAppointments.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCustomers.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search businesses by name, email, or slug..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Businesses Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>All Businesses ({filteredBusinesses.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-medium">Business</th>
                    <th className="text-left p-4 font-medium">Module</th>
                    <th className="text-left p-4 font-medium">Subscription</th>
                    <th className="text-left p-4 font-medium">Status</th>
                    <th className="text-right p-4 font-medium">Appointments</th>
                    <th className="text-right p-4 font-medium">Customers</th>
                    <th className="text-right p-4 font-medium">Revenue</th>
                    <th className="text-right p-4 font-medium">Pending Payouts</th>
                    <th className="text-left p-4 font-medium">Created</th>
                    <th className="text-left p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBusinesses.map((business) => (
                    <tr key={business.id} className="border-b hover:bg-muted/50">
                      <td className="p-4">
                        <div className="font-medium">{business.name}</div>
                        <div className="text-sm text-muted-foreground">{business.owner_email || "No email"}</div>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline" className="capitalize">
                          {business.primary_module || "appointments"}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge variant="outline">
                          {business.plan_name || business.plan_id || "Unknown"}
                        </Badge>
                      </td>
                      <td className="p-4">
                        <Badge variant={business.subscription_status === "active" ? "default" : "secondary"}>
                          {business.subscription_status}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">{business.totalAppointments.toLocaleString()}</td>
                      <td className="p-4 text-right">{business.totalCustomers.toLocaleString()}</td>
                      <td className="p-4 text-right">₵{business.totalRevenue.toLocaleString()}</td>
                      <td className="p-4 text-right">
                        {business.pendingPayouts > 0 ? (
                          <Badge variant="outline" className="text-orange-600">
                            {business.pendingPayouts}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {format(new Date(business.created_at), "MMM d, yyyy")}
                      </td>
                      <td className="p-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setSelectedBusiness(business);
                              setDialogMode("upgrade");
                              setDialogOpen(true);
                            }}>
                              <ArrowUpCircle className="mr-2 h-4 w-4" />
                              Change Plan
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedBusiness(business);
                              setDialogMode("renew");
                              setDialogOpen(true);
                            }}>
                              <RefreshCw className="mr-2 h-4 w-4" />
                              Renew Subscription
                            </DropdownMenuItem>
                            {business.subscription_status === "active" ? (
                              <DropdownMenuItem onClick={() => {
                                setSelectedBusiness(business);
                                setDialogMode("suspend");
                                setDialogOpen(true);
                              }}>
                                <Ban className="mr-2 h-4 w-4" />
                                Suspend Business
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => {
                                setSelectedBusiness(business);
                                setDialogMode("activate");
                                setDialogOpen(true);
                              }}>
                                <CheckCircle2 className="mr-2 h-4 w-4" />
                                Activate Business
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => {
                              navigate(`/app/super-admin/business/${business.id}`);
                            }}>
                              <Eye className="mr-2 h-4 w-4" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => sendWelcomePreview(business)}
                              disabled={previewSendingBusinessId === business.id}
                            >
                              {previewSendingBusinessId === business.id ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Mail className="mr-2 h-4 w-4" />
                              )}
                              Send Welcome Preview
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => void sendPushTestSuite(business, "all")}
                              disabled={pushTestKey === `${business.id}:all`}
                            >
                              {pushTestKey === `${business.id}:all` ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Bell className="mr-2 h-4 w-4" />
                              )}
                              Send push test suite
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => void sendPushTestSuite(business, "owner")}
                              disabled={!business.owner_user_id || pushTestKey === `${business.id}:owner`}
                            >
                              {pushTestKey === `${business.id}:owner` ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Bell className="mr-2 h-4 w-4" />
                              )}
                              Send push tests (owner only)
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <SuperAdminSubscriptionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        business={selectedBusiness}
        mode={dialogMode}
        onSuccess={() => {
          fetchBusinesses();
        }}
      />
    </AppShell>
  );
}
